import {Card, Badge, Row, Col, Alert, Spinner, OverlayTrigger, Tooltip} from 'react-bootstrap'
import {ResumeInstanceModal, PauseInstanceModal, CancelInstanceModal} from './Components'
import {
  CheckCircle,
  Clock,
  ChatSquare,
  Tag,
  ExclamationTriangle,
  XCircle,
  Play,
  StopCircle,
  Hourglass,
  Diagram3,
  CreditCard,
  Pause,
  ReplyAllFill,
} from 'react-bootstrap-icons'
import {useNavigate, useParams} from 'react-router-dom'
import {useI18n} from '../../../../Utils/_helpers/useI18n'
import {
  useCancelInstanceMutation,
  useGetCreditRequiredInstanceQuery,
  useGetInstanceLogsDetailQuery,
  usePauseInstanceMutation,
  useResumeInstanceMutation,
} from '../../../../../services/cellcast.v2'
import {
  IContact,
  IEventLog,
  LOGS_EVENT_TYPE,
  LOGS_STATUS,
} from '../../../../Utils/Constant/FlowConstants'
import {useCallback, useState} from 'react'
import moment from 'moment'
import {toast} from 'react-toastify'
import {renderRowStatus} from './FlowInstance'
import PurchasePopup, {getMinimumCredit} from '../../../../Components/PurchasePopup'

const getEventIcon = (eventType: string) => {
  const iconProps = {size: 16}
  switch (eventType) {
    case 'added_tag':
      return <Tag {...iconProps} />
    case 'TRIGGER_COMPLETED':
      return <Play {...iconProps} />
    case 'SMS_SENT':
      return <ChatSquare {...iconProps} />
    case 'DELAY_STARTED':
      return <Hourglass {...iconProps} />
    case 'DELAY_COMPLETED':
      return <CheckCircle {...iconProps} />
    case 'CONDITION_FAILED':
      return <Diagram3 {...iconProps} />
    case 'INSUFFICIENT_CREDIT':
      return <CreditCard {...iconProps} />
    case 'END_COMPLETED':
      return <StopCircle {...iconProps} />
    case 'RUNNING':
      return <Play {...iconProps} />
    case 'PAUSED':
      return <Pause {...iconProps} />
    case 'CANCELLED':
      return <XCircle {...iconProps} />
    case 'STOP_TO_REPLY':
      return <StopCircle {...iconProps} />
    case LOGS_EVENT_TYPE.ADD_TAG_COMPLETED:
      return <Tag {...iconProps} />
    case LOGS_EVENT_TYPE.ADD_TAG_FAILED:
      return <XCircle {...iconProps} />
    case LOGS_EVENT_TYPE.ADD_TAG_SKIPPED:
      return <Clock {...iconProps} />
    default:
      return <Clock {...iconProps} />
  }
}

const EVENT_VARIANT_MAP: Record<LOGS_EVENT_TYPE, string> = {
  [LOGS_EVENT_TYPE.ADDED_TAG]: 'primary',
  [LOGS_EVENT_TYPE.TRIGGER_COMPLETED]: 'success',
  [LOGS_EVENT_TYPE.SMS_SENT]: 'info',
  [LOGS_EVENT_TYPE.DELAY_STARTED]: 'warning',
  [LOGS_EVENT_TYPE.DELAY_COMPLETED]: 'success',
  [LOGS_EVENT_TYPE.CONDITION_FAILED]: 'warning',
  [LOGS_EVENT_TYPE.INSUFFICIENT_CREDIT]: 'danger',
  [LOGS_EVENT_TYPE.END_COMPLETED]: 'secondary',
  [LOGS_EVENT_TYPE.RUNNING]: 'info',
  [LOGS_EVENT_TYPE.PAUSED]: 'warning',
  [LOGS_EVENT_TYPE.CONDITION_SENT]: 'info',
  [LOGS_EVENT_TYPE.CONDITION_DELIVERED]: 'success',
  [LOGS_EVENT_TYPE.SMS_REPLIED]: 'info',
  [LOGS_EVENT_TYPE.SMS_REPLIED_CONTAIN]: 'info',
  [LOGS_EVENT_TYPE.SMS_REPLIED_START_WITH]: 'info',
  [LOGS_EVENT_TYPE.SMS_REPLIED_END_WITH]: 'info',
  [LOGS_EVENT_TYPE.SMS_REPLIED_EQUAL]: 'info',
  [LOGS_EVENT_TYPE.SMS_FAILED]: 'danger',
  [LOGS_EVENT_TYPE.SMS_DELIVERED]: 'success',
  [LOGS_EVENT_TYPE.CONDITION_REPLY_CONTAIN]: 'info',
  [LOGS_EVENT_TYPE.CONDITION_REPLY_START_WITH]: 'info',
  [LOGS_EVENT_TYPE.CONDITION_REPLY_END_WITH]: 'info',
  [LOGS_EVENT_TYPE.CONDITION_EQUAL]: 'info',
  [LOGS_EVENT_TYPE.CANCELLED]: 'danger',
  [LOGS_EVENT_TYPE.STOP_TO_REPLY]: 'warning',
  [LOGS_EVENT_TYPE.EMAIL_SENT]: 'info',
  [LOGS_EVENT_TYPE.WAIT_FOR_REPLY_STARTED]: 'warning',
  [LOGS_EVENT_TYPE.WAIT_FOR_REPLY_RECEIVED]: 'success',
  [LOGS_EVENT_TYPE.WAIT_FOR_REPLY_TIMEOUT]: 'danger',
  [LOGS_EVENT_TYPE.WAIT_FOR_REPLY_RESUMED]: 'info',
  [LOGS_EVENT_TYPE.ADD_TAG_COMPLETED]: 'success',
  [LOGS_EVENT_TYPE.ADD_TAG_FAILED]: 'danger',
  [LOGS_EVENT_TYPE.ADD_TAG_SKIPPED]: 'warning',
}

const getEventVariant = (eventType: LOGS_EVENT_TYPE) => {
  return EVENT_VARIANT_MAP[eventType] || 'danger'
}

const formatEventType = (eventType: string) => {
  return eventType
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, (l) => l.toUpperCase())
}

const getEventDetails = (event: IEventLog) => {
  if (event?.message) {
    return event.message
  }
}

const formatContactDisplayName = (contact: any) => {
  if (!contact) return {fullName: '', numberOrEmail: '', displayText: 'N/A'}

  let fullName = ''
  const numberOrEmail = contact.number || contact.email || ''

  if (Array.isArray(contact.fields)) {
    const fullNameField = contact.fields.find((f: any) => f.name?.toLowerCase() === 'full name')
    if (fullNameField && fullNameField.value) {
      fullName = fullNameField.value
    } else {
      const firstNameField = contact.fields.find((f: any) => f.name?.toLowerCase() === 'first name')
      const lastNameField = contact.fields.find((f: any) => f.name?.toLowerCase() === 'last name')
      if (firstNameField?.value || lastNameField?.value) {
        fullName = [firstNameField?.value, lastNameField?.value].filter(Boolean).join(' ')
      }
    }
  }

  if (!fullName && contact.name) {
    fullName = contact.name as string
  }

  const displayText = fullName ? fullName : 'No Name'
  const fullDisplay = numberOrEmail ? `${displayText} (${numberOrEmail})` : displayText

  return {
    fullName,
    numberOrEmail,
    displayText,
    fullDisplay,
  }
}

const EventLogsDetail = () => {
  const navigate = useNavigate()
  const {t} = useI18n()
  const {id} = useParams<{id: string}>()

  const [pauseInstance, {isLoading: isPausing}] = usePauseInstanceMutation()
  const [resumeInstance, {isLoading: isResuming}] = useResumeInstanceMutation()
  const [cancelInstance, {isLoading: isCancelling}] = useCancelInstanceMutation()
  const {data: creditData, refetch: refetchCredits} = useGetCreditRequiredInstanceQuery(
    {
      id: id || '',
    },
    {
      skip: !id,
      refetchOnMountOrArgChange: true,
    }
  )

  // Modal states
  const [showResumeModal, setShowResumeModal] = useState(false)
  const [showPauseModal, setShowPauseModal] = useState(false)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [purchasePopupModal, setPurchasePopupModal] = useState(false)
  const [purchaseModelData, setPurchaseModelData] = useState({
    type: 'sms',
    purchaseCredit: 0,
    minimum: 0,
    purpose: 'resume the flow instance',
  })
  const {data, isLoading, error, refetch} = useGetInstanceLogsDetailQuery(
    {id: id || ''},
    {
      skip: !id,
      refetchOnMountOrArgChange: true,
    }
  )

  const navigateBack = useCallback(() => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/automation/logs')
    }
  }, [navigate])

  const handlePauseInstance = async () => {
    if (!id) return
    try {
      await pauseInstance({id}).unwrap()
      refetch()
      setShowPauseModal(false)
    } catch (error) {
      console.error('Failed to pause instance:', error)
    }
  }

  const handleResumeInstance = async () => {
    if (!id) return

    if (creditData?.data) {
      const {requiredCredits, currentCredits} = creditData.data
      if (currentCredits < requiredCredits) {
        setPurchaseModelData({
          type: 'sms',
          purchaseCredit: getMinimumCredit(requiredCredits),
          minimum: requiredCredits,
          purpose: 'resume the flow instance (SMS required)',
        })
        setPurchasePopupModal(true)
        // Keep resume modal open
        return
      }
    }

    try {
      await resumeInstance({id}).unwrap()
      refetch()
      setShowResumeModal(false)
    } catch (error) {
      console.log('🚀 Failed to resume instance:', error)
    }
  }

  const handleCancelInstance = async () => {
    if (!id) return
    try {
      await cancelInstance({id}).unwrap()
      refetch()
      setShowCancelModal(false)
      toast.success(t('FLOW.INSTANCE.CANCEL_INSTANCE_SUCCESS'))
    } catch (error) {
      console.error('Failed to cancel instance:', error)
      toast.error(t('FLOW.INSTANCE.CANCEL_INSTANCE_ERROR'))
    }
  }

  const handlePauseClick = () => {
    setShowPauseModal(true)
  }

  const handleResumeClick = () => {
    setShowResumeModal(true)
  }

  const handleCancelClick = () => {
    setShowCancelModal(true)
  }

  if (isLoading) {
    return (
      <>
        <div className='d-flex justify-content-center align-items-center h-100'>
          <Spinner animation='border' className='text-primary' />
        </div>
      </>
    )
  }
  if (error || !data || !data.data) {
    return <div className='alert alert-danger'>{t('FLOW.EVENT_LOGS.FAILED_TO_FETCH')}</div>
  }
  const eventData = data.data

  const contact = eventData.triggerEvent?.contact as IContact
  const totalDuration =
    new Date(eventData.updatedAt).getTime() - new Date(eventData.createdAt).getTime()
  const durationMinutes = Math.floor(totalDuration / 60000)
  const durationSeconds = Math.floor((totalDuration % 60000) / 1000)

  return (
    <div className='event-logs-detail'>
      <div className='card d-flex mb-4'>
        {/* HEADER SECTION */}
        <div className='card-header px-0 pb-0 border-0 d-flex flex-column gap-4'>
          <div className='d-flex align-items-center gap-3 flex-wrap'>
            <span
              className='me-2'
              style={{cursor: 'pointer'}}
              onClick={navigateBack}
              title={t('COMMON.ACTIONS.BACK')}
            >
              <i className='bi bi-arrow-left fs-4 text-primary'></i>
            </span>
            <div className='mb-0'>
              <h5 className='mb-0'>{t('FLOW.EVENT_LOG_DETAILS')}</h5>
            </div>
            <Badge
              bg={eventData.status === 'completed' ? 'success' : 'warning'}
              className='fs-6 text-uppercase'
            >
              {renderRowStatus(eventData.status)}
            </Badge>

            <div className='ms-auto d-flex gap-2'>
              {(eventData.status === LOGS_STATUS.RUNNING ||
                eventData.status === LOGS_STATUS.WAITING ||
                eventData.status === LOGS_STATUS.PENDING) && (
                <OverlayTrigger
                  placement='top'
                  overlay={<Tooltip>{t('FLOW.INSTANCE.PAUSE_JOB_DESC')}</Tooltip>}
                >
                  <button
                    className='btn btn-sm btn-icon btn-warning'
                    onClick={handlePauseClick}
                    title={t('FLOW.INSTANCE.PAUSE_INSTANCE')}
                    disabled={isPausing}
                  >
                    {isPausing ? (
                      <Spinner animation='border' size='sm' />
                    ) : (
                      <i className='bi bi-pause'></i>
                    )}
                  </button>
                </OverlayTrigger>
              )}

              {eventData.status === LOGS_STATUS.PAUSED && (
                <OverlayTrigger
                  placement='top'
                  overlay={<Tooltip>{t('FLOW.INSTANCE.RESUME_JOB_DESC')}</Tooltip>}
                >
                  <button
                    className='btn btn-sm btn-icon btn-success'
                    onClick={handleResumeClick}
                    title={t('FLOW.INSTANCE.RESUME_INSTANCE')}
                    disabled={isResuming}
                  >
                    {isResuming ? (
                      <Spinner animation='border' size='sm' />
                    ) : (
                      <i className='bi bi-play'></i>
                    )}
                  </button>
                </OverlayTrigger>
              )}

              {(eventData.status === LOGS_STATUS.RUNNING ||
                eventData.status === LOGS_STATUS.WAITING ||
                eventData.status === LOGS_STATUS.PENDING ||
                eventData.status === LOGS_STATUS.PAUSED) && (
                <OverlayTrigger
                  placement='top'
                  overlay={<Tooltip>{t('FLOW.INSTANCE.CANCEL_INSTANCE_DESC')}</Tooltip>}
                >
                  <button
                    className='btn btn-sm btn-icon btn-danger'
                    onClick={handleCancelClick}
                    title={t('FLOW.INSTANCE.CANCEL_INSTANCE')}
                  >
                    <i className='bi bi-x-circle'></i>
                  </button>
                </OverlayTrigger>
              )}
            </div>
          </div>
        </div>

        {/* CONTACT DETAILS SECTION */}
        <div className='card-body px-0 pb-0 border-0 d-flex flex-column'>
          <div className='d-flex flex-column p-4 mb-4'>
            <Row>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.LOGS.CONTACT')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>
                    {(() => {
                      const contact = eventData?.triggerEvent?.contact
                      const contactInfo = formatContactDisplayName(contact)
                      return contactInfo.fullName || contactInfo.displayText
                    })()}
                  </span>
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.LOGS.CONTACT_NUMBER')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>{contact?.number || '-'}</span>
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.LOGS.CONTACT_EMAIL')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>
                    {contact?.fields?.find((f: any) => f.name === 'Email')?.value || '-'}
                  </span>
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.LOGS.CONTACT_ADDRESS')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>
                    {contact?.fields?.find((f: any) => f.name === 'Address')?.value || '-'}
                  </span>
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.EVENT_LOGS.TRIGGER_TYPE')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>
                    {formatEventType(eventData.context?.triggerType)}
                  </span>
                </div>
              </Col>

              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.EVENT_LOGS.IS_REPLIES')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>
                    {eventData?.isReplies ? t('COMMON.YES') : t('COMMON.NO')}
                  </span>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>

      {/* STATUS SECTION */}
      <div className='card p-3 mb-4'>
        <div className='card-body p-2'>
          <Row
            className='text-center justify-content-between align-items-center g-2'
            style={{overflowX: 'auto'}}
          >
            <Col xs={12} sm={6} md={4} lg={2} className='p-1'>
              <Card className='bg-success bg-opacity-10 border-success h-100'>
                <Card.Body className='p-2 d-flex flex-column align-items-center'>
                  <CheckCircle size={18} className='text-success mb-1' />
                  <div className='fw-bold text-success small'>{t('FLOW.EVENT_LOGS.COMPLETED')}</div>
                  <div className='fw-bold text-success fs-5'>
                    {
                      (Array.isArray(eventData.eventLogs) ? eventData.eventLogs : []).filter(
                        (e) =>
                          (e?.type ?? '').includes('COMPLETED') ||
                          (e?.type ?? '').includes('PAUSED') ||
                          (e?.type ?? '').includes('FAILED')
                      ).length
                    }
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} md={4} lg={2} className='p-1'>
              <Card className='bg-info bg-opacity-10 border-info h-100'>
                <Card.Body className='p-2 d-flex flex-column align-items-center'>
                  <ChatSquare size={18} className='text-info mb-1' />
                  <div className='fw-bold text-info small'>{t('FLOW.EVENT_LOGS.SMS_SENT')}</div>
                  <div className='fw-bold text-info fs-5'>
                    {
                      (Array.isArray(eventData.eventLogs) ? eventData.eventLogs : []).filter(
                        (e) => (e?.type ?? '') === 'SMS_SENT'
                      ).length
                    }
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} md={4} lg={2} className='p-1'>
              <Card className='bg-info bg-opacity-10 border-info h-100'>
                <Card.Body className='p-2 d-flex flex-column align-items-center'>
                  <ReplyAllFill size={18} className='mb-1' />
                  <div className='fw-bold small'>{t('FLOW.EVENT_LOGS.BITES')}</div>
                  <div className='fw-bold fs-5'>{eventData?.isReplies ? 1 : 0}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} md={4} lg={2} className='p-1'>
              <Card className='bg-danger bg-opacity-10 border-danger h-100'>
                <Card.Body className='p-2 d-flex flex-column align-items-center'>
                  <ExclamationTriangle size={18} className='text-danger mb-1' />
                  <div className='fw-bold text-danger small'>{t('FLOW.EVENT_LOGS.ERRORS')}</div>
                  <div className='fw-bold text-danger fs-5'>
                    {
                      (Array.isArray(eventData.eventLogs) ? eventData.eventLogs : []).filter((e) =>
                        (e?.type ?? '').includes('INSUFFICIENT')
                      ).length
                    }
                  </div>
                </Card.Body>
              </Card>
            </Col>
            <Col xs={12} sm={6} md={4} lg={2} className='p-1'>
              <Card className='bg-primary bg-opacity-10 border-primary h-100'>
                <Card.Body className='p-2 d-flex flex-column align-items-center'>
                  <Clock size={18} className='text-primary mb-1' />
                  <div className='fw-bold text-primary small'>
                    {t('FLOW.EVENT_LOGS.DURATION_LABEL')}
                  </div>
                  <div className='fw-bold text-primary fs-6'>
                    {durationMinutes}m {durationSeconds}s
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* INFORMATION SECTION */}
      <div className='card d-flex mb-4'>
        {/* INFORMATION DETAILS SECTION */}
        <div className='card-body px-0 pb-0 border-0 d-flex flex-column'>
          <div className='d-flex flex-column p-4 mb-4'>
            <Row>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.LOGS.FLOW')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>{eventData?.flow?.name || '-'}</span>
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.EVENT_LOGS.DURATION')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>
                    {durationMinutes}m {durationSeconds}s
                  </span>
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.EVENT_LOGS.TOTAL_EVENTS')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>{eventData?.eventLogs?.length || 0}</span>
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.EVENT_LOGS.CREATED_AT')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>
                    {moment(eventData.createdAt).local().format('MMM DD YYYY HH:mm:ss')}
                  </span>
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.EVENT_LOGS.UPDATED_AT')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>
                    {moment(eventData.updatedAt).local().format('MMM DD YYYY HH:mm:ss')}
                  </span>
                </div>
              </Col>
              <Col md={4}>
                <div className='mb-3'>
                  <strong className='text-muted fs-5'>{t('FLOW.EVENT_LOGS.STATUS')}:</strong>
                  <span className='fw-bold ms-2 fs-5'>
                    {t(`FLOW.EVENT_LOGS.STATUS.${eventData.status?.toUpperCase()}`)}
                  </span>
                </div>
              </Col>
            </Row>
          </div>
        </div>
      </div>

      <div className='card d-flex flex-column gap-4 p-4 mb-4'>
        <div className='card-body'>
          <div className='d-flex flex-column gap-4'>
            {(Array.isArray(eventData.eventLogs) ? eventData.eventLogs : []).map((event, index) => {
              const type = event?.type ?? ''
              const timestamp = event?.timestamp
                ? moment(event.timestamp).local().format('MMM DD YYYY HH:mm:ss')
                : moment().local().format('MMM DD YYYY HH:mm:ss')
              const data = event?.data

              const renderMessageWithNewlines = (message: string | undefined) => {
                if (!message) return null
                return message.split('\n').map((line, idx) => (
                  <span key={idx}>
                    {line}
                    {idx < message.split('\n').length - 1 && <br />}
                  </span>
                ))
              }

              const eventMessage =
                typeof event?.message === 'string'
                  ? event.message
                  : getEventDetails({
                      ...event,
                      type: type as LOGS_EVENT_TYPE | undefined,
                      timestamp,
                      data,
                    })

              return (
                <div key={index} className='d-flex align-items-start gap-3'>
                  <div className='d-flex flex-column align-items-center'>
                    <div
                      className={`p-2 rounded-circle border border-2 d-flex align-items-center justify-content-center bg-${getEventVariant(
                        type as LOGS_EVENT_TYPE
                      )} bg-opacity-25 border-${getEventVariant(type as LOGS_EVENT_TYPE)}`}
                      style={{minWidth: '40px', minHeight: '40px'}}
                    >
                      {getEventIcon(String(type))}
                    </div>
                    {index < (eventData.eventLogs?.length ?? 0) - 1 && (
                      <div
                        className='border-start border-2 border-secondary opacity-25 mt-2'
                        style={{height: '48px', width: '1px'}}
                      />
                    )}
                  </div>

                  <div className='flex-grow-1'>
                    <div className='d-flex justify-content-between align-items-center mb-2'>
                      <div className='d-flex align-items-center gap-2'>
                        <Badge bg={getEventVariant(type as LOGS_EVENT_TYPE)} className='fs-6'>
                          {formatEventType(type as LOGS_EVENT_TYPE)}
                        </Badge>
                        <span className='text-muted fs-6'>{timestamp ? timestamp : '-'}</span>
                      </div>
                    </div>
                    <div className='fs-5'>
                      {renderMessageWithNewlines(
                        typeof eventMessage === 'string' ? eventMessage : undefined
                      )}
                    </div>

                    {type === 'INSUFFICIENT_CREDIT' && data && (
                      <Alert variant='danger' className='mt-2 mb-0 fs-6'>
                        <div className='d-flex align-items-center gap-2'>
                          <ExclamationTriangle size={18} />
                          <strong>{t('FLOW.EVENT_LOGS.CREDIT_ISSUE')}</strong>
                        </div>
                        <div className='mt-1'>{data.reason}</div>
                      </Alert>
                    )}

                    {type === 'CONDITION_FAILED' && data && (
                      <Alert variant='warning' className='mt-2 mb-0 fs-6'>
                        <div className='d-flex align-items-center gap-2'>
                          <XCircle size={18} />
                          <strong>{t('FLOW.EVENT_LOGS.CONDITION_FAILED')}</strong>
                        </div>
                        <div className='mt-1'>
                          {t('FLOW.EVENT_LOGS.EXPECTED')}:{' '}
                          {typeof data?.condition === 'object' &&
                          data?.condition !== null &&
                          'value' in data.condition
                            ? String((data.condition as {value?: unknown}).value)
                            : '-'}{' '}
                          | {t('FLOW.EVENT_LOGS.BRANCH')}: {data?.branch ?? '-'}
                        </div>
                      </Alert>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Modal Components */}
      <PurchasePopup
        show={purchasePopupModal}
        purchaseModelData={purchaseModelData}
        handleClose={() => {
          setPurchasePopupModal(false)
          // Refetch credits after purchase to get updated balance
          refetchCredits()
        }}
        purpose={purchaseModelData.purpose}
      />
      <ResumeInstanceModal
        show={showResumeModal}
        onHide={() => setShowResumeModal(false)}
        onConfirm={handleResumeInstance}
        isResuming={isResuming}
      />

      <PauseInstanceModal
        show={showPauseModal}
        onHide={() => setShowPauseModal(false)}
        onConfirm={handlePauseInstance}
        isPausing={isPausing}
      />

      <CancelInstanceModal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        onConfirm={handleCancelInstance}
        isCancelling={isCancelling}
      />
    </div>
  )
}

export default EventLogsDetail
